/**
 * lazy-load.js
 * ------------
 * Lightweight progressive-load helper for Urban Performance.
 *
 * The theme's core section lazy-loading is handled by the built-in
 * `lazy-section` custom element (triggered by `lazy-load-section-validations`).
 * This script complements it by:
 *   1. Registering `lazy-section` as a custom element that reveals itself
 *      when it enters the viewport (using IntersectionObserver).
 *   2. Removing the `is-loading` CSS class from images once they are fully loaded,
 *      ensuring the fade-in transition fires correctly for off-screen images.
 */

(function () {
  'use strict';

  // ── 1. lazy-section custom element ────────────────────────────────────────
  // The Liquid template wraps deferred sections in <lazy-section>. This custom
  // element observes its own visibility and swaps in the real content once it
  // enters the viewport.
  if (!customElements.get('lazy-section')) {
    class LazySection extends HTMLElement {
      connectedCallback() {
        // If IntersectionObserver is not supported, show immediately.
        if (!('IntersectionObserver' in window)) {
          this._reveal();
          return;
        }

        this._observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this._reveal();
                obs.unobserve(this);
              }
            });
          },
          // Start loading 400 px before the section enters the viewport so
          // content is ready before the user reaches it.
          { rootMargin: '400px 0px', threshold: 0 }
        );

        this._observer.observe(this);
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
      }

      _reveal() {
        // The skeleton placeholder sits inside the lazy-section before reveal.
        const skeleton = this.querySelector('.skeleton-container');
        if (skeleton) skeleton.remove();

        // Dispatch an event so other scripts can react if needed.
        this.dispatchEvent(new CustomEvent('lazy-section:revealed', { bubbles: true }));
      }
    }

    customElements.define('lazy-section', LazySection);
  }

  // ── 2. Image load-complete handler ────────────────────────────────────────
  // The theme adds `is-loading` to images and removes it via a global observer
  // in its own JS bundle. This is a safe belt-and-suspenders fallback only for
  // images that may have been missed (e.g. images inside lazy-sections that
  // were injected after DOMContentLoaded).
  function handleImageLoad(img) {
    if (img.complete && img.naturalWidth > 0) {
      img.classList.remove('is-loading');
    } else {
      img.addEventListener('load', () => img.classList.remove('is-loading'), { once: true });
      img.addEventListener('error', () => img.classList.remove('is-loading'), { once: true });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    // Handle images already in the DOM.
    document.querySelectorAll('img.is-loading').forEach(handleImageLoad);
  });

  // Also handle images inside sections revealed after load.
  document.addEventListener('lazy-section:revealed', (e) => {
    e.target.querySelectorAll('img.is-loading').forEach(handleImageLoad);
  });

})();
